import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Megaphone,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Edit2,
  Trash2,
  Send,
  Archive,
  Pin,
  Globe,
  Eye,
  Calendar
} from 'lucide-react'
import { announcementApi, type AnnouncementStatus } from '@/api/announcements'
import { TiptapViewer } from '@/components/editor/TiptapViewer'

const STATUS_BADGES: Record<AnnouncementStatus, { bg: string; text: string; label: string }> = {
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: '초안' },
  published: { bg: 'bg-green-100', text: 'text-green-700', label: '게시중' },
  scheduled: { bg: 'bg-blue-100', text: 'text-blue-700', label: '예약' },
  archived: { bg: 'bg-amber-100', text: 'text-amber-700', label: '보관' },
}

const APP_TYPE_LABELS: Record<string, string> = {
  company: '기업 B2B',
  lc: '러닝센터 B2B',
  b2c: 'B2C',
}

export function AnnouncementDetailPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()

  const { data: announcement, isLoading, error } = useQuery({
    queryKey: ['admin', 'announcement', id],
    queryFn: () => announcementApi.getById(Number(id)),
    enabled: !!id,
  })

  const deleteMutation = useMutation({
    mutationFn: announcementApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] })
      navigate('/announcements')
    },
  })

  const publishMutation = useMutation({
    mutationFn: announcementApi.publish,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'announcement', id] })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: announcementApi.archive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'announcement', id] })
    },
  })

  const handleDelete = () => {
    if (announcement && confirm(`정말 "${announcement.title}" 공지사항을 삭제하시겠습니까?`)) {
      deleteMutation.mutate(announcement.announcementId)
    }
  }

  const handlePublish = () => {
    if (announcement && confirm(`"${announcement.title}" 공지사항을 게시하시겠습니까?`)) {
      publishMutation.mutate(announcement.announcementId)
    }
  }

  const handleArchive = () => {
    if (announcement && confirm(`"${announcement.title}" 공지사항을 보관하시겠습니까?`)) {
      archiveMutation.mutate(announcement.announcementId)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">공지사항을 불러오는 중...</p>
      </div>
    )
  }

  if (error || !announcement) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">공지사항을 찾을 수 없습니다</p>
        <button
          onClick={() => navigate('/announcements')}
          className="btn-secondary btn-md mt-4"
        >
          목록으로 돌아가기
        </button>
      </div>
    )
  }

  const statusBadge = STATUS_BADGES[announcement.status]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/announcements')}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Megaphone className="w-7 h-7 text-blue-600" />
              공지사항 상세
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {announcement.status === 'draft' && (
            <button
              onClick={handlePublish}
              disabled={publishMutation.isPending}
              className="btn-primary btn-md inline-flex items-center gap-2"
            >
              {publishMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              게시
            </button>
          )}
          {announcement.status === 'published' && (
            <button
              onClick={handleArchive}
              disabled={archiveMutation.isPending}
              className="btn-secondary btn-md inline-flex items-center gap-2"
            >
              {archiveMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Archive className="w-4 h-4" />
              )}
              보관
            </button>
          )}
          <button
            onClick={() => navigate(`/announcements/${id}/edit`)}
            className="btn-secondary btn-md inline-flex items-center gap-2"
          >
            <Edit2 className="w-4 h-4" />
            수정
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="btn-secondary btn-md inline-flex items-center gap-2 text-red-600 hover:bg-red-50"
          >
            {deleteMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            삭제
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Title & Meta */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {announcement.isPinned && (
                  <Pin className="w-5 h-5 text-amber-500" />
                )}
                <h2 className="text-xl font-bold text-slate-900">{announcement.title}</h2>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                  {statusBadge.label}
                </span>
                <span>작성자: {announcement.authorName}</span>
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {announcement.viewCount.toLocaleString()}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(announcement.createdAt).toLocaleString('ko-KR')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Meta Info */}
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-slate-500">전체 클라이언트 공지:</span>
              <span className="ml-2 font-medium">
                {announcement.isGlobal ? (
                  <span className="inline-flex items-center gap-1 text-green-600">
                    <Globe className="w-4 h-4" /> 예
                  </span>
                ) : (
                  '아니오'
                )}
              </span>
            </div>
            <div>
              <span className="text-slate-500">대상 앱:</span>
              <span className="ml-2 font-medium">
                {announcement.targetAppTypes && announcement.targetAppTypes.length > 0
                  ? announcement.targetAppTypes.map(t => APP_TYPE_LABELS[t] || t).join(', ')
                  : '전체'}
              </span>
            </div>
            <div>
              <span className="text-slate-500">게시 기간:</span>
              <span className="ml-2 font-medium">
                {announcement.publishStartAt
                  ? new Date(announcement.publishStartAt).toLocaleDateString('ko-KR')
                  : '-'}
                {' ~ '}
                {announcement.publishEndAt
                  ? new Date(announcement.publishEndAt).toLocaleDateString('ko-KR')
                  : '무기한'}
              </span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <TiptapViewer content={announcement.content} />
        </div>
      </div>
    </div>
  )
}
